import type React from "react";
import { useEffect } from "react";
import "./LessonsListing.scss";
import { CiShare2, CiStar, CiTimer } from "react-icons/ci";
import { FaPlayCircle } from "react-icons/fa";
import { FiLock } from "react-icons/fi";
import { MdOutlinePlayLesson } from "react-icons/md";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button, FlexboxGrid, Loader, Panel, PanelGroup, Text } from "rsuite";
import { useGetCourseByIdQuery } from "../../entities/Course/courseApi";
import { setCourseId } from "../../entities/Course/courseApi";
import {
	useEnrollCourseMutation,
	useGetUserByIdQuery,
	useUnenrollCourseMutation,
	useVerifyUserQuery,
} from "../../entities/User/userApi";
import { formClass } from "../../shared/helpers";

const LessonsListing: React.FC = () => {
	const dispatch = useDispatch();
	const prefixClass = "lessons-listing";

	const navigate = useNavigate();

	const { id: courseId = "" } = useParams<{ id: string }>();

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (courseId) {
			dispatch(setCourseId(courseId));
			localStorage.setItem("courseId", courseId);
		}
	}, [courseId]);

	const { data: verifiedUser } = useVerifyUserQuery();

	const { data, error, isLoading } = useGetCourseByIdQuery(courseId);

	const { data: userData } = useGetUserByIdQuery(verifiedUser?.user.id ?? "");

	const [entollCourse] = useEnrollCourseMutation();

	const [unenrollCourse] = useUnenrollCourseMutation();

	if (error) {
		return <div>something went wrong</div>;
	}

	if (isLoading) {
		return <Loader center content="loading" vertical />;
	}

	const handleEnroll = async () => {
		const result = await entollCourse({
			courseId,
			userId: userData?._id ?? "",
		});

		if ("data" in result && result.data.status === "success") {
			console.log("Enrolled successfully");
		} else if ("error" in result) {
			console.log("Error enrolling");
		}
	};

	const handleUnenroll = async () => {
		const result = await unenrollCourse({
			courseId,
			userId: userData?._id ?? "",
		});

		if ("data" in result && result.data.status === "success") {
			console.log("Unenrolled successfully");
		} else if ("error" in result) {
			console.log("Error unenrolling");
		}
	};
	const userIsEnrolled = userData?.enrolledCourses?.some(
		(course) => course._id === courseId,
	);

	const goToLessonHandle = (lessonId: string) => {
		if (userIsEnrolled) {
			navigate(`/lessons/${lessonId}`);
		} else {
			navigate(`/courses/${courseId}`);
		}
	};

	return (
		<div className={formClass(prefixClass)}>
			<FlexboxGrid
				className={formClass(prefixClass, "header")}
				justify="space-around"
				align="top"
			>
				<FlexboxGrid.Item>
					<FlexboxGrid justify="center" align="top">
						<div className={formClass(prefixClass, "column")}>
							<div className={formClass(prefixClass, "row")}>
								<p className={formClass(prefixClass, "text")}>
									{data?.course?.name}
								</p>
							</div>
							<div className={formClass(prefixClass, "row", "info")}>
								<div className={formClass(prefixClass, "row", "info-item")}>
									<CiTimer className={formClass(prefixClass, "icon")} />
									<p className={formClass(prefixClass, "text")}>
										Duration: 40 hr
									</p>
								</div>
								<div className={formClass(prefixClass, "row", "info-item")}>
									<MdOutlinePlayLesson
										className={formClass(prefixClass, "icon")}
									/>
									<p className={formClass(prefixClass, "text")}>
										Number of Lessons: 16
									</p>
								</div>
								<div className={formClass(prefixClass, "row", "info-item")}>
									<CiStar className={formClass(prefixClass, "icon")} />
									<p className={formClass(prefixClass, "text")}>
										4.8 (221 reviews)
									</p>
								</div>
							</div>
						</div>
					</FlexboxGrid>
				</FlexboxGrid.Item>
				<FlexboxGrid.Item colspan={12}>
					<FlexboxGrid justify="center" align="middle">
						<div className={formClass(prefixClass, "button-group")}>
							{!userIsEnrolled ? (
								<Button onClick={handleEnroll} appearance="primary" color="red">
									Enroll
								</Button>
							) : (
								<Button
									onClick={handleUnenroll}
									appearance="primary"
									color="red"
								>
									Unenroll
								</Button>
							)}
							<Button
								appearance="primary"
								color="violet"
								startIcon={<CiShare2 />}
							>
								Share
							</Button>
						</div>
					</FlexboxGrid>
				</FlexboxGrid.Item>
			</FlexboxGrid>
			<FlexboxGrid className={formClass(prefixClass, "lesson-list")}>
				<div className={formClass(prefixClass, "left-part")}>
					<PanelGroup
						className={formClass(prefixClass, "panel-group")}
						accordion
						bordered
					>
						{data?.course.lessons?.map((lesson, index) => (
							<Panel header={lesson?.name} key={lesson._id}>
								<FlexboxGrid align="middle">
									<div className={formClass(prefixClass, "lesson-item")}>
										<div
											className={formClass(prefixClass, "lesson-item-avatar")}
										>
											<img src={lesson.image} alt="lesson" />
										</div>
										<Text>{lesson?.description}</Text>
										<div className={formClass(prefixClass, "row")}>
											{!userIsEnrolled ? (
												<Button appearance="subtle" color="green">
													<FiLock />
												</Button>
											) : (
												<Button
													appearance="subtle"
													color="green"
													onClick={() => goToLessonHandle(lesson._id)}
												>
													<FaPlayCircle />
												</Button>
											)}
										</div>
									</div>
								</FlexboxGrid>
							</Panel>
						))}
					</PanelGroup>
				</div>
				<div className={formClass(prefixClass, "right-part")}>
					<img
						src={data?.course.image}
						alt="course cover"
						style={{ maxWidth: "100%", height: "auto" }}
					/>
				</div>
			</FlexboxGrid>
		</div>
	);
};

export default LessonsListing;
